import { useEffect } from 'react';
import { useThree, type RootState } from '@react-three/fiber';
import { Vector3, type Vector3Tuple } from 'three';
import { shallow } from 'zustand/shallow';
import { dispatcher, EditorEventType } from 'modules/editor/events';
import { DEVICE_SIZE } from 'modules/editor/constants';
import { useEditorStore } from 'modules/editor/store';

const positionVector = new Vector3();

export const getTooltipPosition = (
  clientPosition: Vector3Tuple,
  { camera, size }: Pick<RootState, 'camera' | 'size'>
) => {
  const clientPositionCopy = clientPosition.slice() as Vector3Tuple;
  const centerOffset = DEVICE_SIZE * useEditorStore.getState().scaleFactor;
  clientPositionCopy[1] += centerOffset;
  positionVector.set(...clientPositionCopy).project(camera);

  const x = ((positionVector.x + 1) / 2) * size.width + size.left;
  const y = (-(positionVector.y - 1) / 2) * size.height + size.top;
  return { x, y };
};

function setTooltipPosition(camera: RootState['camera'], size: RootState['size']) {
  const hoverDeviceId = useEditorStore.getState().hoverDeviceId;
  if (hoverDeviceId) {
    const clientPosition = useEditorStore.getState().devices[hoverDeviceId].position;
    useEditorStore.setState({
      tooltipPosition: getTooltipPosition(clientPosition, { camera, size }),
    });
  } else {
    useEditorStore.setState({ tooltipPosition: null });
  }
}

export const useTooltip = () => {
  const size = useThree((state) => state.size);
  const camera = useThree((state) => state.camera);

  useEffect(
    () =>
      useEditorStore.subscribe(
        (state) =>
          [
            state.hoverDeviceId && state.devices[state.hoverDeviceId] ?
              state.devices[state.hoverDeviceId].position
            : undefined,
            state.showTooltip,
          ] as const,
        ([clientPosition, showTooltip]) => {
          const tooltipPosition =
            clientPosition && showTooltip ? getTooltipPosition(clientPosition, { camera, size }) : null;
          useEditorStore.setState({ tooltipPosition });
        },
        { equalityFn: shallow }
      ),
    [camera, size]
  );

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;

    const listener = () => {
      clearTimeout(timeout);
      setTooltipPosition(camera, size);

      // for the last render
      timeout = setTimeout(() => setTooltipPosition(camera, size), 100);
    };

    dispatcher.addEventListener(EditorEventType.ZOOM_CHANGED, listener);

    return () => {
      clearTimeout(timeout);
      dispatcher.removeEventListener(EditorEventType.ZOOM_CHANGED, listener);
    };
  }, [camera, size]);
};
